import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LogsService } from '@services/logs';
import { CustomError } from '@shared/models';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { GeoCoordinates, GeoFeatureCollectionDataGouv, GeolocationSearchQueryParams } from '../store-locator/models';
import { LocalitySearch } from './models';

@Injectable({ providedIn: 'root' })
export class LocalitySearchService {
  private readonly http = inject(HttpClient);
  private readonly logsService = inject(LogsService);
  private readonly dataGouvUrl = 'https://data.geopf.fr/geocodage/search/';
  private readonly dataGouvMinScore = 0.5;
  private sessionToken?: google.maps.places.AutocompleteSessionToken;
  private readonly predictionsByPlaceId = new Map<string, google.maps.places.PlacePrediction>();

  /**
   * Fetches locality information from the DataGouv API based on provided search query parameters.
   *
   * @param {GeolocationSearchQueryParams} params - The search query parameters containing geolocation information.
   * @param {boolean} shouldIncludeAddressDetails - Whether the result labels should contain country and zipcode.
   */
  getByDataGouv(
    params: GeolocationSearchQueryParams,
    shouldIncludeAddressDetails = true
  ): Observable<LocalitySearch[] | null> {
    const q = params.q?.trim() ?? '';
    if (q.length < 3) {
      this.logsService.debug('LocalitySearchService', 'Query too short for DataGouv', { q });
      return of(null);
    }

    // Build query parameters
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]!);
    });

    return this.http.get<GeoFeatureCollectionDataGouv>(this.dataGouvUrl, { params: httpParams }).pipe(
      map((res) => {
        const list = res.features;
        if (list.length === 0) {
          this.logsService.debug('LocalitySearchService', 'No DataGouv results', { params });
          return null;
        }

        // DataGouv answers even for a foreign city ("Genève" returns Genevrey, score 0.43), so its own
        // relevance score decides whether to trust it. Never compare its label to the typed text: an
        // unaccented "orleans" does not match "Orléans" and used to fall back to a billed Google call.
        const topScore = Math.max(...list.map((f) => f.properties.score));
        if (topScore < this.dataGouvMinScore) {
          this.logsService.debug('LocalitySearchService', 'DataGouv weak match, falling back to Google', {
            params,
            topScore,
          });
          return null;
        }

        return list.map((f) => ({
          name: shouldIncludeAddressDetails
            ? `${f.properties.label} (${f.properties.postcode}), France`
            : f.properties.label,
          id: f.properties.id,
          coordinates: {
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
          },
          hasGooglePlaceId: false,
        }));
      }),
      catchError((e: unknown) => {
        // Never log the raw error: the nested server.url/message embed the user's typed query (q=...)
        const { status } = e as Partial<CustomError>;
        this.logsService.error('LocalitySearchService', 'DataGouv API error', { status, queryLength: q.length });
        return of(null);
      })
    );
  }

  /**
   * Retrieves locality suggestions from Google Autocomplete based on the provided query parameters.
   * Results are restricted to France, Switzerland, and Belgium.
   *
   * @param {GeolocationSearchQueryParams} params An object containing the search parameters. The `q` property represents
   *                                             the query string for the autocomplete.
   * @param {boolean} shouldIncludeAddressDetails - Whether the result labels should contain country and zipcode.
   */
  getByGoogle(
    params: GeolocationSearchQueryParams,
    shouldIncludeAddressDetails = true
  ): Observable<LocalitySearch[] | null> {
    const queryInput = params.q?.trim() ?? '';

    if (queryInput.length < 3) {
      this.logsService.debug('LocalitySearchService', 'Query too short for Google Autocomplete', { q: queryInput });
      return of(null);
    }

    // Fail fast if Google Maps isn't available
    if (typeof google === 'undefined' || !google.maps) {
      this.logsService.error('LocalitySearchService', 'Google Maps JS API not available');
      return of(null);
    }

    return from(google.maps.importLibrary('places')).pipe(
      switchMap((placesLib) => {
        const { AutocompleteSuggestion } = placesLib;

        const request: google.maps.places.AutocompleteRequest = {
          input: queryInput,
          includedPrimaryTypes: ['locality', 'postal_code', 'sublocality', 'neighborhood'],
          includedRegionCodes: ['FR', 'CH', 'BE'],
          sessionToken: this.#openSession(placesLib),
        };

        return from(
          AutocompleteSuggestion.fetchAutocompleteSuggestions(request) as Promise<{
            suggestions: google.maps.places.AutocompleteSuggestion[];
          }>
        );
      }),
      map((response) => {
        this.logsService.debug('LocalitySearchService', 'Google Autocomplete API Response', response);
        this.predictionsByPlaceId.clear();

        const predictions = response?.suggestions ?? [];
        if (!predictions.length) {
          this.logsService.debug('LocalitySearchService', 'No Google Autocomplete results for query', {
            query: queryInput,
          });
          return null;
        }

        const localitySearchResults: LocalitySearch[] = predictions.map((prediction) => {
          const place = prediction.placePrediction!;
          this.predictionsByPlaceId.set(place.placeId, place);
          const name = this.#splitLocation(place.text?.text);
          return {
            id: place.placeId,
            name: shouldIncludeAddressDetails ? name.display : name.city,
            hasGooglePlaceId: true,
          };
        });

        return localitySearchResults;
      }),
      catchError((error) => {
        this.logsService.error('LocalitySearchService', 'Google Autocomplete error', error);
        return of(null);
      })
    );
  }

  /**
   * Resolves the coordinates of a selected suggestion: a DataGouv one already carries them, a Google
   * one is resolved from its place id. Ends the Google billing session in both cases.
   */
  resolveCoordinates(selected: LocalitySearch): Observable<GeoCoordinates | null> {
    const prediction = this.predictionsByPlaceId.get(selected.id);

    // Any selection ends the session: a token reused across sessions is billed as if none had been
    // sent, so the next search has to open a fresh one.
    this.sessionToken = undefined;

    if (!selected.hasGooglePlaceId) {
      return of(selected.coordinates ?? null);
    }

    if (!prediction) {
      this.logsService.error('LocalitySearchService', 'Google prediction no longer available', { id: selected.id });
      return of(null);
    }

    // fetchFields() on the Place returned by toPlace() carries the token of the autocomplete request,
    // and that is what closes the session. A new Place({ id }) carries none, and Google then bills the
    // suggestions and the details separately.
    return from(prediction.toPlace().fetchFields({ fields: ['location'] })).pipe(
      map(({ place }) => {
        const { location } = place;
        if (!location) {
          this.logsService.error('LocalitySearchService', 'Google place details returned no location', {
            id: selected.id,
          });
          return null;
        }
        return { lat: location.lat(), lng: location.lng() };
      }),
      catchError((error: unknown) => {
        this.logsService.error('LocalitySearchService', 'Google place details error', error);
        return of(null);
      })
    );
  }

  /**
   * Reuses the token of the search in progress, so that Google bills its suggestion requests and the
   * place details that close them as a single session.
   */
  #openSession(placesLib: google.maps.PlacesLibrary): google.maps.places.AutocompleteSessionToken {
    return (this.sessionToken ??= new placesLib.AutocompleteSessionToken());
  }

  #splitLocation(input: string): { city: string; display: string } {
    const [left = '', countryRaw] = input.trim().split(','); // défaut pour éviter TS18048
    const country = countryRaw?.trim();
    const leftPart = left.trim();

    const m = leftPart.match(/^(\d{3,10})\s+(.+)$/u);
    const postal = m?.[1];
    const city = m?.[2] ?? leftPart;

    const display = postal
      ? `${city} (${postal})${country ? `, ${country}` : ''}`
      : `${city}${country ? `, ${country}` : ''}`;

    return { city, display };
  }
}
